import React, { useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Link, useParams, useNavigate } from 'react-router-dom';

import { useSettings } from '../../context/SettingsContext'
import { Nav } from '../partials/Nav';
import { API_URL } from '../Constants';

const Validator = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const settings = useSettings();
  
  const [validator, setValidator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    document.title = 'Validator | ValParx Admin'
    let token = settings.userToken;

    if (token && id) {
      fetchValidator(token, id);
    }
  }, [settings.userToken, id]);

  const fetchValidator = (token, validatorId) => {
    setLoading(true);
    setError('');

    var myHeaders = new Headers();
    myHeaders.append("vp-session-token", token);

    var formdata = new FormData();
    formdata.append("validator_id", validatorId);

    var requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: formdata,
      redirect: 'follow'
    };

    fetch(API_URL + "admin_validatorget", requestOptions)
      .then(response => response.json())
      .then(result => {
        setLoading(false);
        if (result.success) {
          setValidator(result.validator);
        } else {
          setError(result.error || 'Failed to load validator details');
        }
      })
      .catch(error => {
        console.log('error', error);
        setLoading(false);
        setError('An error occurred while fetching validator details');
      });
  };

  const handleApprove = () => {
    if (!settings.userToken || !id) return;
    
    setProcessing(true);

    var myHeaders = new Headers();
    myHeaders.append("vp-session-token", settings.userToken);

    var formdata = new FormData();
    formdata.append("validator_id", id);

    var requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: formdata,
      redirect: 'follow'
    };

    fetch(API_URL + "admin_validatorapprove", requestOptions)
      .then(response => response.json())
      .then(result => {
        setProcessing(false);
        if (result.success) {
          // Redirect back to all validators page
          navigate('/validators');
        } else {
          setError(result.error || 'Failed to approve validator');
          setShowApproveModal(false);
        }
      })
      .catch(error => {
        console.log('error', error);
        setProcessing(false);
        setError('An error occurred while approving validator');
        setShowApproveModal(false);
      });
  };

  const handleDelete = () => {
    if (!settings.userToken || !id) return;
    
    setProcessing(true);

    var myHeaders = new Headers();
    myHeaders.append("vp-session-token", settings.userToken);

    var formdata = new FormData();
    formdata.append("validator_id", id);

    var requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: formdata,
      redirect: 'follow'
    };

    fetch(API_URL + "admin_validatordelete", requestOptions)
      .then(response => response.json())
      .then(result => {
        setProcessing(false);
        if (result.success) {
          // Redirect back to all validators page
          navigate('/validators');
        } else {
          setError(result.error || 'Failed to delete validator');
          setShowDeleteModal(false);
        }
      })
      .catch(error => {
        console.log('error', error);
        setProcessing(false);
        setError('An error occurred while deleting validator');
        setShowDeleteModal(false);
      });
  };

  const getApprovalStatus = (approved) => {
    return approved === "1" || approved === 1 ? (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
        Approved
      </span>
    ) : (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
        Pending Approval
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className='min-h-screen flex items-start justify-center py-12 px-4 sm:px-6 lg:px-8'>
      <Nav />
      <div className="max-w-4xl w-full space-y-8 pt-20">
        <div className='flex justify-between items-center mb-8'>
          <h2 className='mb-0'>Validator Details</h2>
          <Link 
            to="/validators" 
            className="text-accent hover:text-blue-900 inline-flex items-center text-sm"
          >
            <FontAwesomeIcon icon="fa-arrow-left fa-solid" className='mr-2 text-xs' />
            Back to Validators
          </Link>
        </div>

        {error && (
          <div className='bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4'>
            {error}
          </div>
        )}

        {loading ? (
          <p className='text-blueGray-500 items-center flex text-center justify-center'>
            <span>Loading Validator...</span>
            <FontAwesomeIcon icon="fa-solid fa-spinner" className='text-sm ml-2 fa-spin' />
          </p>
        ) : validator ? (
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">{validator.name}</h3>
                {getApprovalStatus(validator.approved)}
              </div>
            </div>

            <div className="px-6 py-6 space-y-6">
              {/* Company Information */}
              <div>
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Company Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Company</label>
                    <p className="mt-1 text-sm text-gray-900">{validator.company}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Validation Method</label>
                    <p className="mt-1 text-sm text-gray-900">{validator.how_work}</p>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Contact Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Phone</label>
                    <p className="mt-1 text-sm text-gray-900">{validator.phone}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Email</label>
                    <p className="mt-1 text-sm text-gray-900">{validator.email}</p>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Address</h4>
                <div className="space-y-2">
                  <p className="text-sm text-gray-900">{validator.address}</p>
                  {validator.address2 && <p className="text-sm text-gray-900">{validator.address2}</p>}
                  <p className="text-sm text-gray-900">
                    {validator.city}, {validator.state} {validator.zip}
                  </p>
                </div>
              </div>

              {/* Additional Details */}
              <div>
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Additional Details</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Will Pay for Validation</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {validator.will_pay === "1" || validator.will_pay === 1 ? 'Yes' : 'No'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Applied On</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(validator.created)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setShowApproveModal(true)}
                disabled={validator.approved === "1" || validator.approved === 1}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {validator.approved === "1" || validator.approved === 1 ? 'Already Approved' : 'Approve Validator'}
              </button>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                Delete Validator
              </button>
            </div>
          </div>
        ) : (
          <p className='text-center font-medium py-8'>Validator not found.</p>
        )}

        {/* Approve Confirmation Modal */}
        {showApproveModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="mb-4">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Approve Validator</h2>
                <p className="text-gray-600">
                  Are you sure you want to approve this validator? This action cannot be reversed at this time.
                </p>
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowApproveModal(false)}
                  disabled={processing}
                  className="py-2 px-6 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  No
                </button>
                <button
                  onClick={handleApprove}
                  disabled={processing}
                  className="py-2 px-6 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {processing ? (
                    <>
                      <FontAwesomeIcon icon="fa-solid fa-spinner" className='fa-spin' />
                      <span>Approving...</span>
                    </>
                  ) : (
                    <span>Yes, Approve</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="mb-4">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Delete Validator</h2>
                <p className="text-gray-600">
                  Are you sure you want to delete this validator? This action cannot be undone.
                </p>
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  disabled={processing}
                  className="py-2 px-6 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  No
                </button>
                <button
                  onClick={handleDelete}
                  disabled={processing}
                  className="py-2 px-6 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {processing ? (
                    <>
                      <FontAwesomeIcon icon="fa-solid fa-spinner" className='fa-spin' />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <span>Yes, Delete</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Validator
